import { useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/pages/home/components/SectionHeading";
import { guides } from "@/mocks/cases";

interface GuideCardProps {
  guide: (typeof guides)[number];
  delay: number;
}

function GuideCard({ guide, delay }: GuideCardProps) {
  const [helpful, setHelpful] = useState(guide.helpful);
  const [voted, setVoted] = useState(false);

  const handleHelpful = () => {
    if (voted) return;
    setHelpful((prev) => prev + 1);
    setVoted(true);
  };

  return (
    <Reveal delay={delay} className="h-full">
      <article className="flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-accent-300">
        <div className="h-44 w-full overflow-hidden">
          <img
            src={guide.cover}
            alt={`避雷指南配图：${guide.title}`}
            title={`${guide.title} · 曝光档案局避雷指南`}
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="flex flex-1 flex-col p-5">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-[11px] font-medium text-accent-900">
            <i className="ri-book-marked-line" />
            {guide.tag}
          </span>
          <h3 className="mt-3 font-heading text-base font-bold leading-snug text-foreground-950 md:text-lg">
            {guide.title}
          </h3>
          <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-600">{guide.excerpt}</p>
          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-4">
            <span className="inline-flex items-center gap-3 text-[11px] text-foreground-500">
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line" />
                {guide.readTime}
              </span>
              <span className="hidden sm:inline-flex">{guide.date}</span>
            </span>
            <button
              type="button"
              onClick={handleHelpful}
              aria-pressed={voted}
              className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-[11px] transition-colors ${
                voted
                  ? "border-primary-500 bg-primary-100 text-primary-700"
                  : "border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              <i className={voted ? "ri-thumb-up-fill" : "ri-thumb-up-line"} />
              有用 {helpful}
            </button>
          </div>
        </div>
      </article>
    </Reveal>
  );
}

export default function GuideSection() {
  return (
    <section id="guide" className="w-full bg-background-50">
      <div className="mx-auto w-full max-w-[1180px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading
          index="04"
          title="避雷指南 · 情绪之后给方法"
          subtitle="看爽之余，更重要的是把案例里的信号用在自己身上。这三篇是读者反馈最实用的内容。"
        />

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 md:gap-6">
          {guides.map((guide, index) => (
            <GuideCard key={guide.id} guide={guide} delay={index * 80} />
          ))}
        </div>

        <p className="mt-8 flex items-center gap-2 text-xs text-foreground-500 md:text-sm">
          <i className="ri-information-line text-base text-primary-600" />
          指南内容仅为经验整理，不构成法律或心理专业意见；涉及纠纷请咨询专业人士。
        </p>
      </div>
    </section>
  );
}